import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { services } from '../data/mock';
import { Music, Mic, Radio, Lightbulb, Camera, Sparkles, Briefcase, Check, ArrowRight } from 'lucide-react';

const iconMap = {
  Music: Music,
  Mic: Mic,
  Radio: Radio,
  Lightbulb: Lightbulb,
  Camera: Camera,
  Sparkles: Sparkles,
  Briefcase: Briefcase
};

const Services = () => {
  const location = useLocation();
  const serviceRefs = useRef({});

  useEffect(() => {
    // Récupérer le slug depuis le hash (ex: #service-beats-production)
    const hash = location.hash;
    if (hash.startsWith('#service-')) {
      const slug = hash.replace('#service-', '');
      const element = serviceRefs.current[slug];
      if (element) {
        // Attendre un peu pour que le DOM soit prêt
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Ajouter la classe de surbrillance
          element.classList.add('ring-4', 'ring-rose-500', 'ring-offset-4', 'ring-offset-black');
          // Retirer après 3 secondes
          setTimeout(() => {
            element.classList.remove('ring-4', 'ring-rose-500', 'ring-offset-4', 'ring-offset-black');
          }, 3000);
        }, 300);
      }
    }
  }, [location]);

  return (
    <section id="services" className="py-16 sm:py-20 lg:py-24 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold uppercase text-white mb-3 sm:mb-4 tracking-wide">
            Services
          </h2>
          <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto px-4">
            Prestations professionnelles pour donner vie à vos projets musicaux
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {services.map((service) => {
            const Icon = iconMap[service.icon];
            return (
              <Link
                key={service.id}
                to={`/service/${service.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                ref={(el) => (serviceRefs.current[service.slug] = el)}
                className={`group relative bg-gradient-to-br from-neutral-900 to-neutral-800 border border-neutral-700 rounded-2xl overflow-hidden hover:border-rose-500/50 transition-all duration-300 hover:transform hover:-translate-y-2 ${
                  service.popular ? 'ring-2 ring-rose-500' : ''
                }`}
              >
                {service.popular && (
                  <div className="absolute top-4 right-4 z-20 px-4 py-1 bg-gradient-to-r from-rose-800 to-violet-800 text-white rounded-full text-xs uppercase tracking-wider font-semibold shadow-lg">
                    Populaire
                  </div>
                )}

                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  
                  {/* Icon Overlay */}
                  <div className="absolute bottom-4 left-4 w-12 h-12 bg-gradient-to-br from-rose-800 to-violet-800 rounded-xl flex items-center justify-center shadow-lg">
                    <Icon size={24} className="text-white" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xl font-bold text-white group-hover:text-rose-400 transition-colors">{service.title}</h3>
                    <ArrowRight size={18} className="text-gray-400 group-hover:text-rose-400 transition-colors" />
                  </div>
                  <p className="text-gray-400 mb-4 text-sm">{service.description}</p>

                  {/* Features */}
                  <ul className="space-y-2 mb-4">
                    {service.features.slice(0, 3).map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-300">
                        <Check size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-xs">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Price */}
                  <div className="pt-4 border-t border-neutral-700">
                    <div className="text-2xl font-bold text-white">{service.price}</div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;